import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "styled-components";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Text } from "../UI";
import { blue } from "../UI/themes";

const MenuItem = ({
  img,
  title,
  selected,
  navigateTo,
  setTitle,
  isAdmin,
  backgroundColor,
  onItemClicked,
  disabled,
  disabledTooltip,
}) => {
  const navigate = useNavigate();
  let theme = useTheme();
  const { t } = useTranslation("menu");
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const [hovered, setHovered] = useState(false);

  if (!!isAdmin) {
    theme = blue;
  }

  // keep the tooltip open while the pointer is over the item OR the tooltip
  // itself (so an interactive popover stays clickable); small delay to bridge
  // the gap between them.
  const openTip = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHovered(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHovered(false), 140);
  };
  useEffect(() => () => closeTimer.current && clearTimeout(closeTimer.current), []);

  // a string tooltip → plain dark bubble; a node → render it as-is (interactive)
  const isNodeTip = disabledTooltip && typeof disabledTooltip !== "string";
  const showTip = disabled && hovered && !!disabledTooltip;
  const rect = showTip && ref.current ? ref.current.getBoundingClientRect() : null;

  const handleMenuItemSelect = (navigateTo, title) => {
    if (disabled) return;
    if (onItemClicked) {
      onItemClicked();
    } else {
      setTitle(title);
      navigate(navigateTo);
    }
  };

  useEffect(() => {
    if (!selected) return;
    setTitle(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <div
      ref={ref}
      onMouseEnter={openTip}
      onMouseLeave={scheduleClose}
      style={{
        display: "flex",
        flexDirection: "column",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        alignSelf: "center",
        gap: 1,
        width: 60,
        height: 58,
        position: "relative",
      }}
      onClick={() => handleMenuItemSelect(navigateTo, title)}>
      {showTip &&
        rect &&
        createPortal(
          <div
            onMouseEnter={openTip}
            onMouseLeave={scheduleClose}
            style={{
              position: "fixed",
              top: rect.top + rect.height / 2,
              left: rect.left - 12,
              transform: "translate(-100%, -50%)",
              zIndex: 3000,
              ...(isNodeTip
                ? { pointerEvents: "auto" }
                : {
                    width: 240,
                    backgroundColor: theme.color.navy,
                    color: theme.color.white,
                    padding: "8px 12px",
                    borderRadius: 10,
                    fontFamily: "Rubik",
                    fontSize: 13,
                    lineHeight: "18px",
                    textAlign: "start",
                    boxShadow: "0px 4px 14px rgba(0,0,0,0.25)",
                    pointerEvents: "none",
                  }),
            }}
          >
            {isNodeTip ? disabledTooltip : t(disabledTooltip)}
          </div>,
          document.body
        )}
      <div
        style={{
          backgroundColor: selected ? "#EDEDFF" : "transparent",
          position: "relative",
          borderRadius: 16,
          width: 60,
          height: 32,
        }}></div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          margin: "0 auto",
          mask: `url(${img}) no-repeat center / contain`,
          backgroundColor: backgroundColor
            ? theme.color.white
            : selected
            ? theme.color.primary
            : theme.color.primary,
          width: 20,
          height: 20,
          marginTop: 3,
        }}></div>
      <Text.H6 style={{ textAlign: "center" }}> {t(title)} </Text.H6>
    </div>
  );
};

export default inject("userStore", "tenantsStore")(observer(MenuItem));
